// src/context/NotificationContext.tsx
import React, { createContext, useContext, useEffect, useState, useCallback, useRef } from "react";
import { getSessionUser } from "../utils/auth/sessionUser";
import { isAppSession, useAuthSession } from "../lib/http/session";
import { notification } from "antd";
import CommunicationsService, { ConnectionState, CommunicationNotification as Notification } from "../services/CommunicationsService";
import { fetchNotifications, markAsRead, updateStatus, deleteNotification, bulkDeleteNotifications, deleteAllNotifications, markAllNotificationsAsRead } from "../api/communications/pushNotificationApi";
import { isHandoverRoute } from "../utils/forms/handoverSession";
import { isQueueAlert } from "../utils/notifications/queueAlert";
import { useOptionalMessagingPanel } from "./MessagingContext";

interface NotificationContextData {
  notifications: Notification[];
  unreadCount: number;
  connectionState: ConnectionState;
  isLoading: boolean;
  userId: string | null;

  // Actions
  markAsRead: (notificationId: string) => Promise<void>;
  updateStatus: (notificationId: string, status: string) => Promise<void>;
  deleteNotification: (notificationId: string) => Promise<void>;
  bulkDelete: (notificationIds: string[]) => Promise<void>;
  deleteAll: () => Promise<void>;
  markAllAsRead: () => Promise<void>;
  refreshNotifications: () => Promise<void>;

  // Queue alert click-through (bell card + toast): mark read, open Messages → Queue, close the bell drawer.
  openQueueFor: (notification: Notification) => void;
  registerNotificationsDrawer: (close: (() => void) | null) => void;

  // Notification methods
  //
  // `options.duration` is antd's, in seconds, and defaults to the 3s every toast has always used.
  // `duration: 0` pins the toast open until it is dismissed by hand — for the rare outcome a
  // clinician must not be allowed to miss while looking away (an invoice issued against stock that
  // was not there). Optional and additive: every existing call site is unaffected.
  showToast: (severity: "success" | "info" | "warn" | "error", summary: string, detail?: string, options?: ToastOptions) => void;
  showSuccess: (message: string, detail?: string) => void;
  showError: (message: string, detail?: string) => void;
  showWarning: (message: string, detail?: string, options?: ToastOptions) => void;
  showInfo: (message: string, detail?: string) => void;
}

/** Per-call toast overrides. `duration` is in SECONDS; `0` means "stays until dismissed". */
export interface ToastOptions {
  duration?: number;
}

const NotificationContext = createContext<NotificationContextData>({
  notifications: [],
  unreadCount: 0,
  connectionState: "disconnected",
  isLoading: false,
  userId: null,
  markAsRead: async () => {},
  updateStatus: async () => {},
  deleteNotification: async () => {},
  bulkDelete: async () => {},
  deleteAll: async () => {},
  markAllAsRead: async () => {},
  refreshNotifications: async () => {},
  openQueueFor: () => {},
  registerNotificationsDrawer: () => {},
  showToast: () => {},
  showSuccess: () => {},
  showError: () => {},
  showWarning: () => {},
  showInfo: () => {},
});

export const NotificationProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [connectionState, setConnectionState] = useState<ConnectionState>("disconnected");
  const [isLoading, setIsLoading] = useState(false);
  const [api, contextHolder] = notification.useNotification();
  const openMessagingPanel = useOptionalMessagingPanel()?.openPanel;
  const closeNotificationsDrawerRef = useRef<(() => void) | null>(null);
  const openQueueForRef = useRef<(notification: Notification) => void>(() => {});

  const user = getSessionUser();
  const isLoggedIn = isAppSession(useAuthSession());
  const userId = user?.id;

  // Safe unread count calculation
  const unreadCount = Array.isArray(notifications) ? notifications.filter((n) => !n.read_status).length : 0;

  // Storage methods for offline persistence
  const getStorageKey = (userId: string) => `notifications_${userId}`;

  const loadStoredNotifications = useCallback(async (userId: string) => {
    try {
      const stored = localStorage.getItem(getStorageKey(userId));
      if (stored) {
        const parsedNotifications = JSON.parse(stored);
        // Validate that parsed data is an array
        if (Array.isArray(parsedNotifications)) {
          setNotifications(parsedNotifications);
        } else {
          console.warn("Stored notifications is not an array, clearing storage");
          localStorage.removeItem(getStorageKey(userId));
          setNotifications([]);
        }
      }
    } catch (error) {
      console.error("Error loading stored notifications:", error);
      // Clear corrupted data
      try {
        localStorage.removeItem(getStorageKey(userId));
      } catch (e) {
        // Ignore cleanup errors
      }
      setNotifications([]);
    }
  }, []);

  const saveNotificationsToStorage = useCallback(async (userId: string, notifs: Notification[]) => {
    try {
      if (!Array.isArray(notifs)) {
        console.warn("Attempted to save non-array notifications");
        return;
      }
      const trimmed = notifs.slice(0, 50); // Keep only last 50 notifications
      localStorage.setItem(getStorageKey(userId), JSON.stringify(trimmed));
    } catch (error) {
      console.error("Error saving notifications to storage:", error);
    }
  }, []);

  // Fetch notifications from API
  const refreshNotifications = useCallback(async () => {
    if (!userId || !isLoggedIn) return;

    setIsLoading(true);
    try {
      const fetchedNotifications = await fetchNotifications(userId);
      // Validate that fetched data is an array
      if (Array.isArray(fetchedNotifications)) {
        setNotifications(fetchedNotifications);
        await saveNotificationsToStorage(userId, fetchedNotifications);
      } else if (fetchedNotifications !== null && fetchedNotifications !== undefined) {
        console.warn("Fetched notifications is not an array:", typeof fetchedNotifications);
        setNotifications([]);
      }
    } catch (error) {
      console.error("Error fetching notifications:", error);
      // Don't clear notifications on fetch error, keep existing ones
    } finally {
      setIsLoading(false);
    }
  }, [userId, isLoggedIn, saveNotificationsToStorage]);

  // WebSocket event handlers
  const handleNotificationReceived = useCallback(
    (notification: Notification) => {
      setNotifications((prev) => {
        // Ensure prev is an array
        const currentNotifications = Array.isArray(prev) ? prev : [];
        
        const exists = currentNotifications.find((n) => n.id === notification.id);
        if (exists) return currentNotifications;

        const updated = [notification, ...currentNotifications];
        if (userId) {
          saveNotificationsToStorage(userId, updated);
        }
        return updated;
      });

      // Show notification for new notification.
      //
      // ⚠️ NOT WHILE THE DEVICE IS HANDED OVER (F10, ClickUp 86d3t4ev9). This provider's
      // antd contextHolder is mounted at the ROOT, outside <Layout> — so on `/patient/form`,
      // which deliberately renders chrome-free, a toast is the one piece of clinical UI that
      // could still appear. The practitioner is still signed in and CommunicationsService is
      // still connected during a hand-over, and `title`/`message` are clinician-authored free
      // text that can name another patient.
      //
      // Only the on-screen popup is suppressed: the notification is still stored above and
      // still raises the bell count the moment the practitioner takes the device back.
      //
      // `window.location` rather than `useLocation`: this fires from a WebSocket callback, so
      // what matters is where the browser is at the instant the toast would appear, not what
      // a closure captured when the subscription was set up.
      const notificationType = notification.type === "error" ? "error" : notification.type === "warning" ? "warning" : "info";
      if (!isHandoverRoute(window.location.pathname, window.location.search)) {
        const toastKey = `notification-${notification.id}`;
        api[notificationType]({
          message: notification.title,
          description: notification.message,
          duration: 5,
          ...(isQueueAlert(notification) && {
            key: toastKey,
            style: { cursor: "pointer" },
            onClick: () => {
              api.destroy(toastKey);
              openQueueForRef.current(notification);
            },
          }),
        });
      }

      // Mark as alerted
      updateStatus(notification.id, "alerted");

      // Auto-delete notification if flag is set
      if (notification.auto_delete) {
        // Delete after toast duration (5 seconds) + small buffer
				deleteNotificationAction(notification.id);

        setTimeout(() => {
          setNotifications((prev) => {
            const currentNotifications = Array.isArray(prev) ? prev : [];
            const updated = currentNotifications.filter((n) => n.id !== notification.id);
            if (userId) {
              saveNotificationsToStorage(userId, updated);
            }
            return updated;
          });
        }, 5500); // 5s toast duration + 500ms buffer
      }
    },
    [userId, saveNotificationsToStorage]
  );

  const handleNotificationStatusChanged = useCallback((updatedNotification: Partial<Notification>) => {
    setNotifications((prev) => {
      const currentNotifications = Array.isArray(prev) ? prev : [];
      return currentNotifications.map((notification) => 
        notification.id === updatedNotification.id 
          ? { ...notification, ...updatedNotification } 
          : notification
      );
    });
  }, []);

  const handleConnectionStateChanged = useCallback(
    (state: ConnectionState) => {
      setConnectionState(state);
      if (state === "connected") {
        refreshNotifications();
      }
    },
    [refreshNotifications]
  );

  // Actions
  const markAsReadAction = useCallback(async (notificationId: string) => {
    try {
      await markAsRead(notificationId);
      setNotifications((prev) => {
        const currentNotifications = Array.isArray(prev) ? prev : [];
        return currentNotifications.map((notification) => 
          notification.id === notificationId 
            ? { ...notification, read_status: true } 
            : notification
        );
      });
    } catch (error) {
      console.error("Error marking notification as read:", error);
    }
  }, []);

  const updateStatusAction = useCallback(async (notificationId: string, status: string) => {
    try {
      await updateStatus(notificationId, status);
      setNotifications((prev) => {
        const currentNotifications = Array.isArray(prev) ? prev : [];
        return currentNotifications.map((notification) => 
          notification.id === notificationId 
            ? { ...notification, status } 
            : notification
        );
      });
    } catch (error) {
      console.error("Error updating notification status:", error);
    }
  }, []);

  const deleteNotificationAction = useCallback(async (notificationId: string) => {
    try {
      await deleteNotification(notificationId);
      setNotifications((prev) => {
        const currentNotifications = Array.isArray(prev) ? prev : [];
        return currentNotifications.filter((n) => n.id !== notificationId);
      });
    } catch (error) {
      console.error("Error deleting notification:", error);
    }
  }, []);

  const bulkDeleteAction = useCallback(async (notificationIds: string[]) => {
    if (!notificationIds.length) return;
    try {
      await bulkDeleteNotifications(notificationIds);
      setNotifications((prev) => {
        const currentNotifications = Array.isArray(prev) ? prev : [];
        const idsSet = new Set(notificationIds);
        const updated = currentNotifications.filter((n) => !idsSet.has(n.id));
        if (userId) saveNotificationsToStorage(userId, updated);
        return updated;
      });
    } catch (error) {
      console.error("Error bulk deleting notifications:", error);
      throw error;
    }
  }, [userId, saveNotificationsToStorage]);

  const deleteAllAction = useCallback(async () => {
    if (!userId) return;
    try {
      await deleteAllNotifications(userId);
      setNotifications([]);
      localStorage.removeItem(getStorageKey(userId));
    } catch (error) {
      console.error("Error deleting all notifications:", error);
      throw error;
    }
  }, [userId]);

  const markAllAsReadAction = useCallback(async () => {
    if (!userId) return;
    try {
      await markAllNotificationsAsRead(userId);
      setNotifications((prev) => {
        const currentNotifications = Array.isArray(prev) ? prev : [];
        const updated = currentNotifications.map((n) => ({ ...n, read_status: true, status: "seen" }));
        saveNotificationsToStorage(userId, updated);
        return updated;
      });
    } catch (error) {
      console.error("Error marking all notifications as read:", error);
      throw error;
    }
  }, [userId, saveNotificationsToStorage]);

  const registerNotificationsDrawer = useCallback((close: (() => void) | null) => {
    closeNotificationsDrawerRef.current = close;
  }, []);

  const openQueueFor = useCallback(
    (n: Notification) => {
      if (!isQueueAlert(n) || !openMessagingPanel) return;
      if (!n.read_status) markAsReadAction(n.id);
      openMessagingPanel({ tab: "queue", highlightConversationId: n.related_entity_id });
      closeNotificationsDrawerRef.current?.();
    },
    [openMessagingPanel, markAsReadAction]
  );
  // The toast handler is registered once per socket session; read the latest handler through a ref.
  openQueueForRef.current = openQueueFor;

  // Notification methods
  const showToast = useCallback((severity: "success" | "info" | "warn" | "error", summary: string, detail?: string, options?: ToastOptions) => {
    const type = severity === "warn" ? "warning" : severity;
    api[type]({
      message: summary,
      description: detail,
      duration: options?.duration ?? 3,
    });
  }, [api]);

  const showSuccess = useCallback(
    (message: string, detail?: string) => {
      showToast("success", message, detail);
    },
    [showToast]
  );

  const showError = useCallback(
    (message: string, detail?: string) => {
      showToast("error", message, detail);
    },
    [showToast]
  );

  const showWarning = useCallback(
    (message: string, detail?: string, options?: ToastOptions) => {
      showToast("warn", message, detail, options);
    },
    [showToast]
  );

  const showInfo = useCallback(
    (message: string, detail?: string) => {
      showToast("info", message, detail);
    },
    [showToast]
  );

  // Setup WebSocket connection
  useEffect(() => {
    if (!isLoggedIn || !userId) {
      // Do NOT call CommunicationsService.disconnect() here — MessagingContext owns
      // the connection lifecycle. Just clear local notification state.
      setNotifications([]);
      setConnectionState("disconnected");
      return;
    }

    // Load stored notifications first
    loadStoredNotifications(userId);

    // Register notification handlers under an isolated namespace so they never
    // overwrite handlers registered by MessagingContext or useMessaging.
    CommunicationsService.registerListeners("notifications", {
      onNotificationReceived: handleNotificationReceived,
      onNotificationStatusChanged: handleNotificationStatusChanged,
      onConnectionStateChanged: handleConnectionStateChanged,
    });

    // Connect — safe no-op if MessagingContext already established the connection.
    CommunicationsService.connect(userId);

    // Initial data fetch
    refreshNotifications();

    return () => {
      CommunicationsService.unregisterListeners("notifications");
    };
  }, [isLoggedIn, userId, loadStoredNotifications, handleNotificationReceived, handleNotificationStatusChanged, handleConnectionStateChanged, refreshNotifications]);

  // Handle visibility change
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible" && userId && isLoggedIn) {
        if (CommunicationsService.getConnectionState() === "disconnected") {
          CommunicationsService.connect(userId);
        }
        refreshNotifications();
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => document.removeEventListener("visibilitychange", handleVisibilityChange);
  }, [userId, isLoggedIn, refreshNotifications]);

  const contextValue: NotificationContextData = {
    notifications,
    unreadCount,
    connectionState,
    isLoading,
    userId,
    markAsRead: markAsReadAction,
    updateStatus: updateStatusAction,
    deleteNotification: deleteNotificationAction,
    bulkDelete: bulkDeleteAction,
    deleteAll: deleteAllAction,
    markAllAsRead: markAllAsReadAction,
    refreshNotifications,
    openQueueFor,
    registerNotificationsDrawer,
    showToast,
    showSuccess,
    showError,
    showWarning,
    showInfo,
  };

  return (
    <NotificationContext.Provider value={contextValue}>
      {contextHolder}
      {children}
    </NotificationContext.Provider>
  );
};

export const useNotifications = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error("useNotifications must be used within NotificationProvider");
  }
  return context;
};